
import Vue from "vue";
import * as lodash from "lodash";
import SlideTagMenu from "@/components/organisms/SlideTagMenu.vue";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/SimpleDialog.vue";
import Chip from "@/components/atoms/chips/Chip.vue";
import TagIcon from "@/components/atoms/TagIcon.vue";
import EditorController from "@/components/molecules/EditorController.vue";
import MemoVersionsDialog, { MemoVersionsDialogProps } from "@/components/ui/MemoVersionsDialog.vue";
import HistoryIcon from "icons/History.vue";
import { Memo, MemoTag } from "@/lib/models";
import { MemoStorage } from "@/lib/storages";
import { Dialog } from "@/lib/utils";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";

// ソートの種類
const CountType = {
  onlyCharacter: "countTypeAll",
  addBlank: "countTypeWithSpaces",
  addNewLine: "countTypeWithNewlines",
  all: "countTypeWithSpacesAndNewlines",
} as const;

// enumは使わない方がいいみたいだからunion型にしてみた
// eslint-disable-next-line no-redeclare
type CountType = typeof CountType[keyof typeof CountType];

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { SlideTagMenu, Chip, TagIcon, EditorController, HistoryIcon },
  created() {
    this.initialize();
    // テーマ変更を検知するMutationObserverを設定
    if (typeof window !== "undefined") {
      this.themeWatcher = new MutationObserver(() => {
        // テーマ変更時にcomputed propertyを再計算させるため、$forceUpdateを呼び出す
        this.$forceUpdate();
      });
      this.themeWatcher.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-app-theme"],
      });
    }
  },
  beforeDestroy() {
    // コンポーネントが破棄される前に保存ボタンのショートカットキーを解除
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();
    // MutationObserverをクリーンアップ
    if (this.themeWatcher) {
      this.themeWatcher.disconnect();
      this.themeWatcher = null;
    }
  },
  data() {
    const editingMemo = lodash.cloneDeep(this.$store.getters["memoModule/memo"](this.memoId));
    const currentCountType = CountType.onlyCharacter; // "countTypeAll"
    return {
      editingMemo,
      content: "",
      preContent: "",
      currentCountType,
      unbindShortcutKeys: null,
      isSaving: false,
      themeWatcher: null as MutationObserver | null,
      defaultColor: "#a0a0a0",
      activeColor: "#5698e1",
      isVersionsDialogOpen: false,
    };
  },
  computed: {
    restoredMemoContent(): string | null {
      return this.$store.getters["memoModule/restoredMemoContent"];
    },
    /** 他コンポーネントとの id 重複を避けるためメモ用 textarea は固定 id を使用 */
    memoTextareaId() {
      return "memo-textarea";
    },
    hasMemoTags() {
      const memoTags = this.selectedMemoTags;
      if (!memoTags) return false;
      if (memoTags.length === 0) return false;

      return true;
    },
    selectedMemoTags() {
      if (this.editingMemo.items) {
        const memoTags = this.editingMemo.items.map((item) => this.memoTags.find((tag) => tag.id === item));
        return memoTags.filter((memoTag): memoTag is MemoTag => typeof memoTag !== "undefined");
      }

      return [];
    },
    counter() {
      const content = lodash.clone(this.content);
      let contentLength = 0;
      switch (this.currentCountType) {
        case CountType.addBlank:
          contentLength = content.replace(/\n/g, "").length;
          break;
        case CountType.addNewLine:
          contentLength = content.replace(/[^\S\n]/g, "").length;
          break;
        case CountType.all:
          contentLength = content.length;
          break;
        case CountType.onlyCharacter:
        default:
          contentLength = content.replace(/\s+|\n/g, "").length;
          break;
      }

      return this.$t("memoPage.memoDetail.characterCounter", { count: contentLength });
    },
    memo() {
      return lodash.cloneDeep(this.$store.getters["memoModule/memo"](this.memoId));
    },
    memoTags() {
      return this.$store.state.memoModule.memoTags;
    },
    iconColor() {
      // CSS変数からアイコン色を取得
      if (typeof window !== "undefined") {
        const computedStyle = getComputedStyle(document.documentElement);
        return computedStyle.getPropertyValue("--color-icon-primary").trim() || "#404040";
      }
      return "#404040";
    },
    selectArrowIcon() {
      // CSS変数からアイコン色を取得してSVGを生成
      const color = this.iconColor.replace("#", "%23");
      return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${color}' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`;
    },
    countTypesArray() {
      return [CountType.onlyCharacter, CountType.addBlank, CountType.addNewLine, CountType.all];
    },
    countTypes() {
      return [
        this.$t("memoPage.memoDetail.countTypeAll"),
        this.$t("memoPage.memoDetail.countTypeWithSpaces"),
        this.$t("memoPage.memoDetail.countTypeWithNewlines"),
        this.$t("memoPage.memoDetail.countTypeWithSpacesAndNewlines"),
      ];
    },
  },
  methods: {
    async initialize() {
      const { memo, onUpdatedIsChanged } = this;

      this.$store.dispatch("memoModule/initializeRestoredMemoContent");

      // 既存のショートカットキーリスナーを解除してから再登録する（メモ切り替え時のリスナー蓄積を防ぐ）
      if (this.unbindShortcutKeys) this.unbindShortcutKeys();

      // 保存ボタンのショートカットキーの有効化と解除関数の取得
      this.unbindShortcutKeys = handleShortcutKeys([
        { trigger: TriggerKey.Meta, keys: ["s"], callback: this.updateMemo },
        { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.updateMemo },
      ]);

      if (memo) {
        const memoStorage = new MemoStorage();
        this.editingMemo = lodash.cloneDeep(memo);
        this.content = await memoStorage.downloadFile(memo);
        this.preContent = lodash.cloneDeep(this.content);
        // eslint-disable-next-line no-unused-expressions
        onUpdatedIsChanged && onUpdatedIsChanged(false);
      } else {
        // 該当するものがない場合は初期値を入れる
        this.editingMemo = {
          id: this.memoId,
          novelId: "",
          title: "",
          items: [],
          s3BucketPath: "",
          createdAt: "",
          updatedAt: "",
        };

        this.content = "";
        this.preContent = "";
        // eslint-disable-next-line no-unused-expressions
        onUpdatedIsChanged && onUpdatedIsChanged(false);
      }
    },
    change() {
      const { onUpdatedIsChanged } = this;

      if (this.memo) {
        if (
          this.memo.title !== this.editingMemo.title ||
          this.preContent !== this.content ||
          this.modifiedNovelId(this.memo.novelId, this.editingMemo.novelId) ||
          this.modifiedMemoTags(this.memo.items, this.editingMemo.items)
        )
          // eslint-disable-next-line no-unused-expressions
          onUpdatedIsChanged && onUpdatedIsChanged(true);
        else {
          // eslint-disable-next-line no-unused-expressions
          onUpdatedIsChanged && onUpdatedIsChanged(false);
        }
      }
    },
    async updateMemo() {
      const { $store, isChanged, preContent, content, onUpdatedIsChanged } = this;

      if (!isChanged || this.isSaving) return;

      this.isSaving = true;
      this.editingMemo.items = this.selectedMemoTags.map((memoTag) => memoTag.id);

      try {
        await $store.dispatch("memoModule/updateMemo", {
          memo: this.editingMemo,
          content: preContent !== content ? content : null,
          callback: () => {
            this.preContent = lodash.cloneDeep(content);
            // eslint-disable-next-line no-unused-expressions
            onUpdatedIsChanged && onUpdatedIsChanged(false);

            this.$notify({
              title: this.$t("common.saved"),
              type: "success",
            });
          },
        });
      } finally {
        this.isSaving = false;
      }
    },
    deleteMemo() {
      this.$store.dispatch("memoModule/deleteMemo", {
        id: this.memoId,
        callback: this.deleteCallBack,
      });
    },
    deleteCallBack() {
      this.$notify({
        title: this.$t("memoPage.deleted"),
        type: "error",
      });

      if (Object.prototype.hasOwnProperty.call(this.$route.params, "novelId")) {
        this.$router.push({
          name: this.$route.name as string,
          params: { novelId: this.novelId },
        });
        return;
      }
      if (Object.prototype.hasOwnProperty.call(this.$route.query, "novelId")) {
        this.$router.push({
          name: this.$route.name as string,
          query: { novelId: this.novelId },
        });
        return;
      }

      this.$router.push({ name: this.$route.name as string });
    },
    onSaveButtonClick() {
      this.updateMemo();
    },
    async showVersions() {
      const { memoId, content } = this;
      const versionsDialog = new Dialog(MemoVersionsDialog);
      const data: MemoVersionsDialogProps = { memoId, content };
      this.isVersionsDialogOpen = true;
      await versionsDialog.show(data);
      this.isVersionsDialogOpen = false;
    },
    onDeleteButtonClick() {
      const confirmDialog = new Dialog(SimpleDialog);
      const data: SimpleDialogProps = {
        title: this.$t("memoPage.memoDetail.deleteTitle"),
        content: this.$t("memoPage.memoDetail.deleteContent"),
        positive: this.$t("common.delete"),
        positiveCallback: () => this.deleteMemo(),
      };
      confirmDialog.show(data);
    },
    modifiedNovelId(a, b) {
      let x = "";
      let y = "";
      if (a !== undefined && a !== null) x = a;

      if (b !== undefined && b !== null) y = b;

      return x !== y;
    },
    modifiedMemoTags(a, b) {
      let x: string[] = [];
      let y: string[] = [];

      if (a !== undefined && a !== null) x = lodash.cloneDeep(a);

      if (b !== undefined && b !== null) y = lodash.cloneDeep(b);

      return !lodash.isEqual(x.sort(), y.sort());
    },
  },
  props: {
    memoId: String,
    novelId: String,
    isChanged: Boolean,
    inNovel: {
      type: Boolean,
      default: false,
    },
    onUpdatedIsChanged: Function,
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    $route: "initialize",
    async restoredMemoContent(value: string | null) {
      if (value == null || !this.memoId) return;

      // 未保存の変更がある場合は先に保存してから復元する（ManuscriptEditor.vue の restoredContent と同様）
      if (this.isChanged) {
        await this.updateMemo();
      }

      // 復元時は content のみ更新する（preContent は触らない）。
      // preContent も同じ値にすると updateMemo() で preContent !== content が false になり
      // content: null が store に渡り、S3 へのアップロードがスキップされて復元内容が保存されなくなる。
      this.content = value;
      this.$store.dispatch("memoModule/initializeRestoredMemoContent");
      const { onUpdatedIsChanged } = this;
      if (onUpdatedIsChanged) onUpdatedIsChanged(true);
    },
  },
});

interface Data {
  editingMemo: Memo;
  content: string;
  preContent: string;
  currentCountType: CountType;
  unbindShortcutKeys: (() => void) | null;
  isSaving: boolean;
  themeWatcher: MutationObserver | null;
  defaultColor: string;
  activeColor: string;
  isVersionsDialogOpen: boolean;
}

interface Computed {
  restoredMemoContent: string | null;
  memoTextareaId: string;
  hasMemoTags: boolean;
  selectedMemoTags: MemoTag[];
  counter: string;
  memo: Memo;
  memoTags: MemoTag[];
  iconColor: string;
  selectArrowIcon: string;
  countTypesArray: CountType[];
  countTypes: string[];
}

interface Methods {
  initialize: (updated?: boolean) => void;
  change: () => void;
  updateMemo: () => Promise<void>;
  deleteMemo: () => void;
  showVersions: () => Promise<void>;
  modifiedNovelId: (a: null | undefined | string, b: null | undefined | string) => boolean;
  modifiedMemoTags: (a: null | undefined | string[], b: null | undefined | string[]) => boolean;
  onDeleteButtonClick: () => void;
  onSaveButtonClick: () => void;
  deleteCallBack: () => void;
}

interface Props {
  memoId: string;
  novelId: string;
  inNovel: boolean;
  isChanged: boolean;
  onUpdatedIsChanged?: (isChanged: boolean) => void;
  readonly: boolean;
}
